import { ref, inject } from 'vue';
export function useProductActions() {
    const showCopyToast = ref(false);
    let toastTimeout = null;
    const apiBaseUrl = inject('apiBaseUrl', '');
    // Copy to clipboard with toast notification
    const copyToClipboard = async (text, toastMessage = 'Code copied!') => {
        try {
            await navigator.clipboard.writeText(text);
            // Clear any existing timeout
            if (toastTimeout) {
                clearTimeout(toastTimeout);
            }
            // Show toast
            showCopyToast.value = true;
            // Hide toast after 3 seconds
            toastTimeout = setTimeout(() => {
                showCopyToast.value = false;
            }, 3000);
            return true;
        }
        catch (err) {
            console.error('Failed to copy to clipboard:', err);
            return false;
        }
    };
    // Print page (browser chrome / current document)
    const printPage = () => {
        window.print();
    };
    const postRecommendationPdf = async (payload) => {
        if (!apiBaseUrl) {
            console.error('apiBaseUrl not provided for PDF');
            throw new Error('PDF is not available.');
        }
        const url = `${apiBaseUrl.replace(/\/$/, '')}/recommendations/pdf`;
        const res = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/pdf, application/json;q=0.9',
                'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify(payload),
        });
        if (!res.ok) {
            let message = res.statusText;
            const contentType = res.headers.get('content-type') || '';
            try {
                if (contentType.includes('application/json')) {
                    const errBody = await res.json();
                    if (errBody?.message) {
                        message = errBody.message;
                    }
                }
                else {
                    const text = await res.text();
                    if (text) {
                        message = text.length > 280 ? `${text.slice(0, 280)}…` : text;
                    }
                }
            }
            catch {
                // ignore
            }
            throw new Error(message || 'Could not generate PDF.');
        }
        return res.blob();
    };
    /**
     * Downloads the DomPDF output as a file (save dialog / Downloads folder).
     */
    const downloadRecommendationPdf = async (payload) => {
        const blob = await postRecommendationPdf(payload);
        const pdfBlob = blob.type.includes('pdf')
            ? blob
            : new Blob([blob], { type: 'application/pdf' });
        const objectUrl = URL.createObjectURL(pdfBlob);
        const anchor = document.createElement('a');
        anchor.href = objectUrl;
        anchor.download = 'recommendations.pdf';
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        URL.revokeObjectURL(objectUrl);
    };
    /**
     * Opens the same DomPDF output as download in a hidden iframe and triggers the browser print dialog.
     */
    const printRecommendationPdf = async (payload) => {
        const blob = await postRecommendationPdf(payload);
        const pdfBlob = blob.type.includes('pdf')
            ? blob
            : new Blob([blob], { type: 'application/pdf' });
        const objectUrl = URL.createObjectURL(pdfBlob);
        const iframe = document.createElement('iframe');
        iframe.setAttribute('style', 'position:fixed;width:0;height:0;border:0;left:-9999px;visibility:hidden');
        iframe.setAttribute('aria-hidden', 'true');
        document.body.appendChild(iframe);
        let printInvoked = false;
        const cleanup = () => {
            URL.revokeObjectURL(objectUrl);
            iframe.remove();
        };
        const invokePrint = () => {
            if (printInvoked) {
                return;
            }
            printInvoked = true;
            try {
                iframe.contentWindow?.focus();
                iframe.contentWindow?.print();
            }
            catch {
                // ignore (e.g. blocked viewer)
            }
            // Allow the print dialog / PDF viewer to release the blob URL
            window.setTimeout(cleanup, 2000);
        };
        iframe.onload = () => {
            invokePrint();
        };
        iframe.src = objectUrl;
        // Some browsers do not fire load for embedded PDF; still try print after a short delay
        window.setTimeout(() => {
            invokePrint();
        }, 750);
    };
    // Share product/recommendation
    const share = async (data) => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: data.title || 'Share',
                    text: data.text || '',
                    url: data.url || window.location.href,
                });
            }
            catch (err) {
                console.error('Error sharing:', err);
            }
        }
        else {
            // Fallback: copy URL to clipboard
            await copyToClipboard(data.url || window.location.href, 'Link copied!');
        }
    };
    // Cleanup function
    const cleanup = () => {
        if (toastTimeout) {
            clearTimeout(toastTimeout);
        }
    };
    return {
        printPage,
        printRecommendationPdf,
        downloadRecommendationPdf,
        share,
        cleanup,
    };
}
